import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { ResolvedInputDef } from './cell-detail-helpers';
import {
  LocationInput,
  type LocationResolvedValue,
} from '@/components/builder/LocationInput';

export type InputDataType =
  | 'percentage'
  | 'currency'
  | 'currency_per_sf'
  | 'currency_per_unit_year'
  | 'decimal'
  | 'integer'
  | 'date'
  | 'string'
  | (string & {}); // fallback union catch-all

export interface InputCellEditorProps {
  def: ResolvedInputDef;
  /** Current committed value from `inputValues[namedRange]`. */
  currentValue: unknown;
  /** Disabled while a mutation is in flight. */
  pending: boolean;
  /** Submit handler — receives the normalized value for non-location dataTypes. */
  onSubmit: (value: unknown) => void;
  /**
   * Location-resolved callback (ADR 0027, Session 4). Called INSTEAD of
   * `onSubmit` for `dataType:'location'` inputs — carries the full
   * `{placeId, formattedAddress, sessionToken}` payload so the parent can
   * fire `useModelEdit` with the complete location vars.
   *
   * When absent the location branch falls back to `onSubmit(v.formattedAddress)`
   * (Session-3 regression-safe: non-location call sites that don't pass this
   * prop continue to work correctly).
   */
  onLocationResolved?: (v: LocationResolvedValue) => void;
  /** Inline error to render below the editor (server validation). Cleared by the parent after the user edits the field again. */
  error: string | null;
  /** When true, the input + Save + Cancel are all disabled (billing read-only at 100% usage). */
  readOnly?: boolean;
  /**
   * Active conversation id — required for the `location` dataType branch so
   * `<LocationInput>` can surface the must-select Google Places picker inline.
   *
   * Threaded from `InputBranch` in `CellDetailPanel` which already owns
   * `conversationId` — NOT introduced as an improvised Context.
   */
  conversationId?: string | null;
}

/**
 * Inline editor for a single input cell. Adapts the control + parse
 * normalization to `InputDefinition.dataType`:
 *
 *   percentage             → number input + `%` suffix; on submit `value/100`
 *   currency / per-SF / per-unit-year → number input + `$` prefix
 *   decimal                → number input step=0.01
 *   integer                → number input step=1; on submit `Math.round(value)`
 *   date                   → date input; submit ISO yyyy-mm-dd
 *   string / unknown       → text input; submit raw string
 *
 * Save / Cancel are local — the parent owns the mutation. On Cancel, the
 * editor reverts to the committed `currentValue`. The parent re-syncs
 * `currentValue` after a successful Realtime echo (Session 4 plumbing).
 */
export function InputCellEditor({
  def,
  currentValue,
  pending,
  onSubmit,
  onLocationResolved,
  error,
  readOnly = false,
  conversationId,
}: InputCellEditorProps) {
  const dataType = (def.dataType ?? 'string') as InputDataType;
  const initial = useInitialDisplayValue(currentValue, dataType);
  const [draft, setDraft] = useState<string>(initial);
  // When the committed `currentValue` changes (e.g. Realtime echo arrived),
  // re-sync the draft so the editor reflects the latest committed value.
  // Comparing by JSON-stringified initial is fine — values are scalar.
  const initialRef = useRef(initial);
  useEffect(() => {
    if (initial !== initialRef.current) {
      initialRef.current = initial;
      setDraft(initial);
    }
  }, [initial]);

  // ---------------------------------------------------------------------------
  // Location branch — EARLY RETURN (ADR 0024, divergence #4: early return NOT
  // a new switch arm; placed AFTER all hooks above so React hook order is
  // stable across renders — Rules of Hooks compliant).
  //
  // `(string & {})` fallback in InputDataType already absorbs 'location' but
  // today falls through to the 'number' inputType ternary (WRONG). This branch
  // intercepts it first (research §4 VERBATIM).
  //
  // readOnly: mirrors scalar read-only — shows the committed value as disabled
  // text; does NOT mount the picker (user can't edit in billing read-only mode).
  //
  // Session 4 owns threading the sessionToken from onResolved to the owner-
  // write server hook; Session 3's contract is commit-via-onSubmit only.
  // ---------------------------------------------------------------------------
  if (dataType === 'location') {
    if (readOnly) {
      // Read-only location: display the resolved address as disabled text,
      // mirroring the scalar input's `disabled` state.
      return (
        <div className="space-y-2" data-testid="input-cell-editor">
          <div className="flex items-center gap-2 bg-input-bg border border-input-border rounded-[3px] px-2.5 py-1.5">
            <input
              type="text"
              value={(currentValue as string) ?? ''}
              disabled
              aria-label={def.displayName ?? def.name}
              data-testid="input-cell-editor-input"
              className="flex-1 min-w-0 bg-transparent text-text-bright text-sm font-mono outline-none border-none disabled:opacity-50"
              readOnly
            />
          </div>
        </div>
      );
    }

    return (
      <LocationInput
        conversationId={conversationId ?? ''}
        inputKey={def.name}
        initialValue={(currentValue as string) ?? null}
        onResolved={(v) => {
          // ADR 0027 (Session 4): when the parent wires `onLocationResolved`,
          // hand the full resolved payload up so `useModelEdit` can carry
          // placeId + sessionToken to the server hook. Fall back to the
          // Session-3 path (formattedAddress only) when the prop is absent so
          // call sites that don't need location enrichment keep working.
          if (onLocationResolved) {
            onLocationResolved(v);
          } else {
            onSubmit(v.formattedAddress);
          }
        }}
        onCancel={() => {
          /* No-op: the parent owns cancel flow. */
        }}
        autoFocus
      />
    );
  }

  const dirty = draft !== initial;
  const canSubmit = dirty && !pending && !readOnly;

  const handleSave = () => {
    if (!canSubmit) return;
    const normalized = normalizeForSubmit(draft, dataType);
    if (normalized === undefined) {
      // Local parse failure — surface inline by re-rendering the field as
      // an error banner. The server will re-validate; a stricter local
      // parse error here would be redundant. Pass through as raw string
      // so the server's `NORMALIZE_FAILED` path runs.
      onSubmit(draft);
      return;
    }
    onSubmit(normalized);
  };

  const handleCancel = () => {
    setDraft(initial);
  };

  // Per-dataType control wiring.
  const inputType =
    dataType === 'date' ? 'date' : dataType === 'string' ? 'text' : 'number';
  const step =
    dataType === 'integer'
      ? '1'
      : dataType === 'decimal'
      ? '0.01'
      : dataType === 'percentage'
      ? '0.01'
      : undefined;
  const prefix =
    dataType === 'currency' ||
    dataType === 'currency_per_sf' ||
    dataType === 'currency_per_unit_year'
      ? '$'
      : null;
  const suffix =
    dataType === 'percentage'
      ? '%'
      : dataType === 'currency_per_sf'
      ? '/SF'
      : dataType === 'currency_per_unit_year'
      ? '/unit/yr'
      : null;

  return (
    <div className="space-y-2" data-testid="input-cell-editor">
      <div
        className={clsx(
          'flex items-center gap-2 bg-input-bg border rounded-[3px] px-2.5 py-1.5',
          error
            ? 'border-danger/40'
            : 'border-input-border focus-within:border-accent/40'
        )}
      >
        {prefix && (
          <span className="text-text-dim font-mono text-sm shrink-0">
            {prefix}
          </span>
        )}
        <input
          type={inputType}
          step={step}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
          disabled={pending || readOnly}
          aria-label={def.displayName ?? def.name}
          data-testid="input-cell-editor-input"
          className="flex-1 min-w-0 bg-transparent text-text-bright text-sm font-mono outline-none border-none disabled:opacity-50"
        />
        {suffix && (
          <span className="text-text-dim font-mono text-sm shrink-0">
            {suffix}
          </span>
        )}
      </div>
      {error && (
        <div
          className="text-xs font-mono text-danger leading-snug"
          role="alert"
          data-testid="input-cell-editor-error"
        >
          {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          data-testid="input-cell-editor-save"
          className={clsx(
            'px-3 py-1 text-xs font-mono rounded-[2px] cursor-pointer border',
            canSubmit
              ? 'bg-accent/15 border-accent/40 text-accent hover:bg-accent/25'
              : 'bg-transparent border-panel-border text-text-dim cursor-not-allowed'
          )}
        >
          {pending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={pending || !dirty || readOnly}
          data-testid="input-cell-editor-cancel"
          className={clsx(
            'px-3 py-1 text-xs font-mono rounded-[2px] cursor-pointer border bg-transparent border-panel-border',
            !pending && dirty
              ? 'text-text hover:text-text-bright'
              : 'text-text-dim cursor-not-allowed'
          )}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Render the committed `currentValue` as the initial editable string,
 * matching the units/format the dataType expects on input. e.g.
 * `currentValue: 0.058` with `dataType: 'percentage'` renders `"5.8"`
 * (so the user types percentage points, not the raw fraction).
 *
 * Pure — exported for unit testing.
 */
export function useInitialDisplayValue(
  value: unknown,
  dataType: InputDataType
): string {
  if (value === null || value === undefined) return '';
  if (dataType === 'percentage' && typeof value === 'number') {
    // Convert raw fraction (e.g. 0.058) → human-friendly percentage (5.8).
    // Round to a reasonable precision so the rebound from 5.8 → 0.058 →
    // re-display yields the same digits.
    return String(Math.round(value * 100 * 100) / 100);
  }
  if (dataType === 'integer' && typeof value === 'number') {
    return String(Math.round(value));
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * Parse + normalize the editor's draft for submit. Returns `undefined`
 * when the draft can't be parsed by the local rules — caller passes the
 * raw string through so the server's `NORMALIZE_FAILED` path runs.
 *
 * Pure — exported for unit testing.
 */
export function normalizeForSubmit(
  draft: string,
  dataType: InputDataType
): unknown {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  switch (dataType) {
    case 'percentage': {
      const n = Number.parseFloat(trimmed);
      if (!Number.isFinite(n)) return undefined;
      return n / 100;
    }
    case 'integer': {
      const n = Number.parseFloat(trimmed);
      if (!Number.isFinite(n)) return undefined;
      return Math.round(n);
    }
    case 'currency':
    case 'currency_per_sf':
    case 'currency_per_unit_year':
    case 'decimal': {
      const n = Number.parseFloat(trimmed);
      if (!Number.isFinite(n)) return undefined;
      return n;
    }
    case 'date':
      return trimmed; // ISO yyyy-mm-dd from `<input type="date">`
    default:
      return trimmed; // string fallback
  }
}
